import React from "react";
import type { ScoreRange } from "../../types/ScoringInstruction.type";
import type { ValidationResult } from "../../utils/scoringValidation";
import { ScoringInstructionsRow } from "./ScoringInstructionsRow";

interface ScoringInstructionsTableProps {
	ranges: ScoreRange[];
	totalScore: number;
	validation: ValidationResult;
	onDeleteRange: (id: string) => void;
	onMoveRange: (id: string, direction: "up" | "down") => void;
	onToggleRangeType: (id: string) => void;
	onUpdateRange: (id: string, updates: Partial<ScoreRange>) => void;
}

export const ScoringInstructionsTable: React.FC<ScoringInstructionsTableProps> = ({
	ranges,
	totalScore,
	validation,
	onUpdateRange,
	onDeleteRange,
	onMoveRange,
	onToggleRangeType,
}) => {
	if (ranges.length === 0) {
		return (
			<div className="rounded border border-gray-200 p-8 text-center">
				<p className="font-roboto text-sm text-gray-500">
					No score ranges defined. Click &quot;Add Row&quot; to add one.
				</p>
			</div>
		);
	}

	return (
		<div className="overflow-x-auto rounded border border-gray-200">
			<table className="w-full" data-testid="scoring-instructions-table">
				<thead>
					<tr className="border-b border-gray-200 bg-gray-50">
						<th className="w-12 px-3 py-2 text-left font-roboto text-xs font-semibold uppercase text-gray-500">
							#
						</th>
						<th className="w-56 px-3 py-2 text-left font-roboto text-xs font-semibold uppercase text-gray-500">
							Score
						</th>
						<th className="px-3 py-2 text-left font-roboto text-xs font-semibold uppercase text-gray-500">
							Description
						</th>
						<th className="w-28 px-3 py-2 text-left font-roboto text-xs font-semibold uppercase text-gray-500">
							Actions
						</th>
					</tr>
				</thead>
				<tbody>
					{ranges.map((range, index) => (
						<ScoringInstructionsRow
							key={range.id}
							range={range}
							index={index}
							isFirst={index === 0}
							isLast={index === ranges.length - 1}
							totalScore={totalScore}
							error={validation.errors[range.id]}
							onUpdate={(updates) => onUpdateRange(range.id, updates)}
							onDelete={() => onDeleteRange(range.id)}
							onMove={(direction) => onMoveRange(range.id, direction)}
							onToggleType={() => onToggleRangeType(range.id)}
						/>
					))}
				</tbody>
			</table>
		</div>
	);
};
